import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ArrowNarrowRightIcon } from '@/components/icons/ArrowNarrowRightIcon';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { ProgressBar } from '@/components/progress/ProgressBar/ProgressBar';
import { romie, ROMIE_BOLD_WEIGHT } from '@/styles/fonts/romie';
import { COLORS, GRAY } from '@/styles/tokens/colors';
import { TITLE_SERIF_SIZING } from '@/styles/tokens/fonts';

import {
  DeliverablesCard,
  DeliverablesCardProps,
  PresentationFailureIndicator,
} from './SetupDeliverablesCard';
import { SetupPresentationArtwork } from './SetupPresentationArtwork';

const setupBannerGradient =
  'linear-gradient(90deg, rgba(17, 24, 39, 0) 0%, rgba(17, 24, 39, 0.04) 100%)';

export function ProcessingPanel({
  householdName,
  progress,
  running,
  failed,
  deliverables,
}: {
  householdName: string;
  progress: number;
  running: boolean;
  failed: boolean;
  deliverables: DeliverablesCardProps;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          lg: 'minmax(0, 1fr) minmax(0, 1fr)',
        },
        alignItems: 'center',
        gap: { xs: 3, lg: 8 },
        minHeight: { lg: 226 },
        p: { xs: 2.5, md: 3 },
        mx: -2,
        mt: failed ? 0 : -2,
        borderBottom: `1px solid ${GRAY[100]}`,
        borderRadius: 0,
        bgcolor: COLORS.PRIMITIVES.WHITE,
        backgroundImage: setupBannerGradient,
      }}
    >
      <Stack spacing={1} useFlexGap>
        <Typography
          variant="h1_display"
          component="h2"
          sx={{
            fontFamily: romie.style.fontFamily,
            fontWeight: ROMIE_BOLD_WEIGHT,
            ...TITLE_SERIF_SIZING.m,
          }}
        >
          Building the {householdName} client household
        </Typography>
        <Typography variant="body-s">
          Luminary is creating the client profile
          {deliverables.presentation === 'processing' &&
            (deliverables.presentationCount &&
            deliverables.presentationCount > 1
              ? ' and generating presentations'
              : ' and generating the presentation')}
          . This takes a few minutes, and you can explore other areas of the
          platform while you wait.
        </Typography>
        <ProgressBar
          variant="determinate"
          aria-label="Household setup in progress"
          aria-valuetext={
            running ? `${Math.round(progress)}% estimated` : 'Complete'
          }
          value={progress}
          sx={{
            mt: 0.5,
            '&.MuiLinearProgress-colorPrimary': { background: GRAY[200] },
            '& .MuiLinearProgress-bar': {
              background: GRAY[600],
              transition: 'transform 500ms ease-out',
            },
            '@media (prefers-reduced-motion: reduce)': {
              '& .MuiLinearProgress-bar': { transition: 'none' },
            },
          }}
        />
      </Stack>
      <DeliverablesCard {...deliverables} />
    </Box>
  );
}

export function CompletionHero({
  hasPresentation,
  presentationFailed = false,
  presentationCount = 1,
  onDismiss,
  onViewPresentations,
  onReviewSuggestions,
  suggestionCount,
}: {
  hasPresentation: boolean;
  presentationFailed?: boolean;
  presentationCount?: number;
  onDismiss: () => void;
  onViewPresentations: () => void;
  onReviewSuggestions?: () => void;
  suggestionCount?: number;
}) {
  let heading = 'Client profile is ready';
  if (hasPresentation && !presentationFailed) {
    heading =
      presentationCount > 1
        ? 'Presentations are ready'
        : 'Your presentation is ready';
  }
  return (
    <Box
      role="region"
      aria-labelledby="setup-completion-heading"
      sx={{
        position: 'relative',
        mx: -2,
        mt: -2,
        bgcolor: COLORS.PRIMITIVES.WHITE,
        backgroundImage: setupBannerGradient,
        borderBottom: `1px solid ${GRAY[100]}`,
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          lg: 'minmax(0, 1fr) minmax(0, 540px)',
        },
        columnGap: 4,
        p: { xs: 3, md: 4 },
        minHeight: { xs: 260, md: 330 },
        overflow: 'hidden',
      }}
    >
      {!presentationFailed && (
        <IconButton
          ariaLabel="Dismiss setup completion"
          icon={XCloseIcon}
          variant="transparent"
          size="xs"
          onClick={onDismiss}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1,
          }}
        />
      )}
      <Stack
        spacing={1.5}
        useFlexGap
        justifyContent="center"
        sx={{ pr: { xs: 3, lg: 0 }, minWidth: 0 }}
      >
        <Typography
          id="setup-completion-heading"
          aria-live="polite"
          variant="title-xl"
          component="h2"
          sx={{
            fontFamily: romie.style.fontFamily,
            fontWeight: ROMIE_BOLD_WEIGHT,
            ...TITLE_SERIF_SIZING.xl,
          }}
        >
          {heading}
        </Typography>
        <Typography variant="body-m" sx={{ maxWidth: 512 }}>
          {hasPresentation && !presentationFailed
            ? 'Built from the client profile. Some presentations may have data suggestions to review before they’re complete.'
            : 'The client profile has been created from the household’s documents and information.'}
        </Typography>
        {presentationFailed && <PresentationFailureIndicator />}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems="flex-start"
          sx={{ mt: 0.5 }}
        >
          {(hasPresentation || presentationFailed) && (
            <Button variant="primary" size="md" onClick={onViewPresentations}>
              {presentationCount === 1 && !presentationFailed
                ? 'View presentation'
                : 'View presentations'}
            </Button>
          )}
          {onReviewSuggestions &&
            suggestionCount !== undefined &&
            suggestionCount > 0 && (
              <Button
                variant={hasPresentation ? 'secondary' : 'primary'}
                size="md"
                endIcon={ArrowNarrowRightIcon}
                onClick={onReviewSuggestions}
              >
                Review {suggestionCount}{' '}
                {suggestionCount === 1 ? 'suggestion' : 'suggestions'}
              </Button>
            )}
        </Stack>
      </Stack>
      <SetupPresentationArtwork />
    </Box>
  );
}
